<template>
  <div class="container-fluid px-4 py-4" style="background:#f8fafc;min-height:100vh;">

    <!-- Header -->
    <div class="d-flex align-items-center justify-content-between flex-wrap pb-3 mb-4" style="gap:12px;">
      <h4 class="text-dark mb-0" style="font-size:22px;font-weight:600;letter-spacing:-0.3px;">
        {{ greeting }}
      </h4>
    </div>

    <!-- Quick actions -->
    <div class="row mb-4">
      <div class="col-6 col-md-3 mb-2">
        <a href="/quotes/create" class="ov-quick-action btn btn-outline-primary">
          <i class="bi bi-file-earmark-text mr-2"></i>Create estimate
        </a>
      </div>
      <div class="col-6 col-md-3 mb-2">
        <a href="/invoices/create" class="ov-quick-action btn btn-outline-secondary">
          <i class="bi bi-receipt mr-2"></i>Create invoice
        </a>
      </div>
      <div class="col-6 col-md-3 mb-2">
        <a href="/payment-transactions/ledger" class="ov-quick-action btn btn-outline-primary">
          <i class="bi bi-arrow-left-right mr-2"></i>Add transaction
        </a>
      </div>
      <div class="col-6 col-md-3 mb-2">
        <a href="/payment-transactions/ledger" class="ov-quick-action btn btn-outline-secondary">
          <i class="bi bi-cash-stack mr-2"></i>Add bill
        </a>
      </div>
    </div>

    <div class="d-flex align-items-center justify-content-between flex-wrap mb-3" style="gap:8px;position:relative;">
      <h6 class="text-dark mb-0" style="font-weight:600;">Insights for you</h6>

      <div ref="customizeWrap" style="position:relative;">
        <div v-if="showCustomize" class="ov-customize-panel">
          <p class="text-dark mb-0" style="font-weight:600;font-size:13px;">Choose an accounting method</p>
          <p class="text-secondary mb-2" style="font-size:11px;">For profit &amp; loss, expenses breakdown, and net income</p>
          <a href="#" class="ov-customize-option" @click.prevent="setAccountingMethod('accrual')">
            <i class="bi bi-check" :style="{ visibility: accountingMethod === 'accrual' ? 'visible' : 'hidden' }"></i>
            Accrual (paid &amp; unpaid)
          </a>
          <a href="#" class="ov-customize-option" @click.prevent="setAccountingMethod('cash')">
            <i class="bi bi-check" :style="{ visibility: accountingMethod === 'cash' ? 'visible' : 'hidden' }"></i>
            Cash basis (paid)
          </a>
          <a href="/reports" class="d-flex align-items-center justify-content-between text-dark mt-2" style="font-size:12px;font-weight:500;">
            Learn more <i class="bi bi-box-arrow-up-right"></i>
          </a>
        </div>
        <button class="btn btn-outline-secondary btn-sm" style="border-radius:20px;font-size:12px;" @click="showCustomize = !showCustomize">
          Customize <i class="bi bi-sliders ml-1"></i>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center text-secondary py-5">
      <span class="spinner-border spinner-border-sm mr-2"></span>Loading your overview…
    </div>

    <div v-else-if="error" class="bg-white border" style="border-radius:14px;border-color:#e9ecef !important;padding:24px;">
      <p class="text-dark mb-1" style="font-size:13px;font-weight:500;">Couldn't load your overview</p>
      <p class="text-secondary mb-3" style="font-size:12px;">{{ error }}</p>
      <button class="btn btn-sm btn-dark" style="border-radius:8px;" @click="fetchOverview">Retry</button>
    </div>

    <div v-else class="row align-items-stretch">
      <!-- Overdue invoices and bills -->
      <div class="col-12 col-lg-6 mb-4">
        <div class="bg-white border h-100" style="border-radius:14px;border-color:#e9ecef !important;padding:20px;">
          <p class="text-dark mb-3" style="font-weight:600;font-size:14px;">Overdue invoices and bills <i class="bi bi-question-circle text-secondary" style="font-size:12px;" title="Invoices and supplier bills past their due date, most overdue first."></i></p>

          <div class="d-flex align-items-center justify-content-between mb-2">
            <span class="text-secondary" style="font-size:12px;">
              <i class="bi bi-receipt mr-1"></i>Overdue invoices ({{ overdueInvoices.length }})
            </span>
            <a href="/reports/receivables" class="text-dark" style="font-size:12px;font-weight:600;">View all</a>
          </div>
          <div v-if="overdueInvoices.length === 0" class="text-secondary mb-3" style="font-size:12px;">No overdue invoices.</div>
          <a
            v-for="row in overdueInvoices" :key="row.invoice_id"
            :href="`/invoices/${row.invoice_id}`"
            class="d-flex justify-content-between align-items-center py-2 ov-overdue-row"
            style="border-top:1px solid #f1f5f9;"
          >
            <div>
              <p class="mb-0 text-dark" style="font-size:13px;font-weight:500;">{{ row.customer_name }}</p>
              <p class="mb-0 text-secondary" style="font-size:11px;">Overdue {{ overdueLabel(row.days_overdue) }}</p>
            </div>
            <span class="text-dark" style="font-size:13px;font-weight:600;">{{ formatCurrency(row.balance) }}</span>
          </a>

          <div class="d-flex align-items-center justify-content-between mt-4 mb-2">
            <span class="text-secondary" style="font-size:12px;">
              <i class="bi bi-cash-stack mr-1"></i>Overdue bills ({{ overdueBills.length }})
            </span>
            <a href="/reports/payables" class="text-dark" style="font-size:12px;font-weight:600;">View all</a>
          </div>
          <div v-if="overdueBills.length === 0" class="text-secondary" style="font-size:12px;">No overdue bills.</div>
          <a
            v-for="row in overdueBills" :key="row.purchase_id"
            :href="`/purchases?purchase-id=${row.purchase_id}`"
            class="d-flex justify-content-between align-items-center py-2 ov-overdue-row"
            style="border-top:1px solid #f1f5f9;"
          >
            <div>
              <p class="mb-0 text-dark" style="font-size:13px;font-weight:500;">{{ row.supplier_name }}</p>
              <p class="mb-0 text-secondary" style="font-size:11px;">Overdue {{ overdueLabel(row.days_overdue) }}</p>
            </div>
            <span class="text-dark" style="font-size:13px;font-weight:600;">{{ formatCurrency(row.balance) }}</span>
          </a>
        </div>
      </div>

      <!-- Cash flow -->
      <div class="col-12 col-lg-6 mb-4">
        <div class="bg-white border h-100" style="border-radius:14px;border-color:#e9ecef !important;padding:20px;">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <p class="text-dark mb-0" style="font-weight:600;font-size:14px;">Cash flow <i class="bi bi-question-circle text-secondary" style="font-size:12px;" title="Real cash actually received and paid out — not accrual revenue/expense."></i></p>
            <a href="/reports" class="text-dark" style="font-size:12px;font-weight:600;">View report</a>
          </div>
          <div class="d-flex align-items-center justify-content-between flex-wrap mb-3" style="gap:8px;">
            <div class="d-flex align-items-center" style="gap:14px;font-size:12px;color:#64748b;">
              <span><span class="d-inline-block mr-1" style="width:8px;height:8px;border-radius:2px;background:#fd4719;"></span>Inflow</span>
              <span><span class="d-inline-block mr-1" style="width:8px;height:8px;border-radius:2px;background:#e2e8f0;"></span>Outflow</span>
              <span><span class="d-inline-block mr-1" style="width:8px;height:8px;border-radius:50%;background:#1e293b;"></span>Net change</span>
            </div>
            <select class="form-control form-control-sm ov-period-select" v-model="trendPeriod" @change="fetchOverview">
              <option v-for="o in trendPeriodOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
            </select>
          </div>
          <div style="height:240px;width:100%;position:relative;"><canvas ref="cashFlowCanvas"></canvas></div>
        </div>
      </div>

      <!-- Profit and loss -->
      <div class="col-12 col-lg-6 mb-4">
        <div class="bg-white border h-100" style="border-radius:14px;border-color:#e9ecef !important;padding:20px;">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <p class="text-dark mb-0" style="font-weight:600;font-size:14px;">Profit and loss <i class="bi bi-question-circle text-secondary" style="font-size:12px;" :title="accountingMethod === 'cash' ? 'Cash basis — revenue counted only when paid.' : 'Accrual basis — revenue counted at sale, whether paid or not.'"></i></p>
            <a href="/reports" class="text-dark" style="font-size:12px;font-weight:600;">View report</a>
          </div>
          <div class="d-flex align-items-center justify-content-between flex-wrap mb-3" style="gap:8px;">
            <div class="d-flex align-items-center" style="gap:14px;font-size:12px;color:#64748b;">
              <span><span class="d-inline-block mr-1" style="width:8px;height:8px;border-radius:2px;background:#fd4719;"></span>Income</span>
              <span><span class="d-inline-block mr-1" style="width:8px;height:8px;border-radius:2px;background:#1e293b;"></span>Expenses</span>
            </div>
            <select class="form-control form-control-sm ov-period-select" v-model="trendPeriod" @change="fetchOverview">
              <option v-for="o in trendPeriodOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
            </select>
          </div>
          <div style="height:240px;width:100%;position:relative;"><canvas ref="plCanvas"></canvas></div>
        </div>
      </div>

      <!-- Expenses breakdown -->
      <div class="col-12 col-lg-6 mb-4">
        <div class="bg-white border h-100" style="border-radius:14px;border-color:#e9ecef !important;padding:20px;">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <p class="text-dark mb-0" style="font-weight:600;font-size:14px;">Expenses breakdown <i class="bi bi-question-circle text-secondary" style="font-size:12px;" title="Where your expenses went this period, by category."></i></p>
            <select class="form-control form-control-sm ov-period-select" v-model="breakdownPeriod" @change="fetchOverview">
              <option v-for="o in breakdownPeriodOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
            </select>
          </div>
          <div v-if="expenseCategories.length === 0" class="text-secondary" style="font-size:12px;">No expenses recorded yet.</div>
          <div v-else class="d-flex align-items-center flex-wrap" style="gap:24px;">
            <div class="flex-grow-1" style="min-width:140px;">
              <div v-for="(cat, idx) in expenseCategories" :key="cat.name" class="d-flex align-items-center justify-content-between mb-2">
                <span class="d-flex align-items-center" style="font-size:12px;color:#475569;">
                  <span class="d-inline-block mr-2" :style="{ width: '8px', height: '8px', borderRadius: '50%', background: chartColors[idx % chartColors.length] }"></span>
                  {{ cat.name }}
                </span>
                <span class="text-dark" style="font-size:12px;font-weight:600;">{{ cat.percent }}%</span>
              </div>
            </div>
            <div style="height:180px;width:180px;position:relative;flex-shrink:0;"><canvas ref="expenseCanvas"></canvas></div>
          </div>
        </div>
      </div>

      <!-- Net income -->
      <div class="col-12 col-lg-6 mb-4">
        <div class="bg-white border h-100" style="border-radius:14px;border-color:#e9ecef !important;padding:20px;">
          <div class="d-flex align-items-center justify-content-between flex-wrap mb-3" style="gap:8px;">
            <p class="text-dark mb-0" style="font-weight:600;font-size:14px;">Net income</p>
            <div class="d-flex align-items-center flex-wrap" style="gap:6px;">
              <span class="text-secondary" style="font-size:12px;">Comparison by:</span>
              <select class="form-control form-control-sm ov-period-select" v-model="comparisonPeriod" @change="fetchOverview">
                <option v-for="o in comparisonPeriodOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
              </select>
              <i class="bi bi-question-circle text-secondary" title="Compares the current period to the one before it, at the granularity you choose."></i>
            </div>
          </div>
          <div style="height:160px;width:100%;position:relative;"><canvas ref="netIncomeCanvas"></canvas></div>
          <table class="w-100 mt-3" style="font-size:12px;">
            <thead>
              <tr class="text-secondary">
                <th></th>
                <th class="text-right font-weight-normal">Previous</th>
                <th class="text-right font-weight-normal">Current</th>
              </tr>
            </thead>
            <tbody class="text-dark">
              <tr><td>Income</td><td class="text-right">{{ formatCurrency(previousMonth.income) }}</td><td class="text-right">{{ formatCurrency(currentMonth.income) }}</td></tr>
              <tr><td>Expense</td><td class="text-right">{{ formatCurrency(previousMonth.expense) }}</td><td class="text-right">{{ formatCurrency(currentMonth.expense) }}</td></tr>
              <tr class="font-weight-bold"><td>Net Income</td><td class="text-right">{{ formatCurrency(previousMonth.net_income) }}</td><td class="text-right">{{ formatCurrency(currentMonth.net_income) }}</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Payable and owing -->
      <div class="col-12 col-lg-6 mb-4">
        <div class="bg-white border h-100" style="border-radius:14px;border-color:#e9ecef !important;padding:20px;">
          <p class="text-dark mb-3" style="font-weight:600;font-size:14px;">Payable and owing <i class="bi bi-question-circle text-secondary" style="font-size:12px;" title="Outstanding invoices owed to you and bills you owe, by how overdue they are."></i></p>
          <div class="row">
            <div class="col-12 col-sm-6 mb-3 mb-sm-0">
              <p class="text-secondary mb-2" style="font-size:12px;">Invoices payable to you</p>
              <table class="w-100" style="font-size:12px;">
                <tbody class="text-dark">
                  <tr v-for="b in agingRows(receivables)" :key="'ar-'+b.label">
                    <td class="py-1" :class="b.label === 'Coming due' ? 'text-primary font-weight-600' : ''">{{ b.label }}</td>
                    <td class="py-1 text-right">{{ formatCurrency(b.amount) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="col-12 col-sm-6">
              <p class="text-secondary mb-2" style="font-size:12px;">Bills you owe</p>
              <table class="w-100" style="font-size:12px;">
                <tbody class="text-dark">
                  <tr v-for="b in agingRows(payables)" :key="'ap-'+b.label">
                    <td class="py-1" :class="b.label === 'Coming due' ? 'text-primary font-weight-600' : ''">{{ b.label }}</td>
                    <td class="py-1 text-right">{{ formatCurrency(b.amount) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Projects -->
      <div v-if="companySettings && companySettings.has_projects" class="col-12 col-lg-6 mb-4">
        <div class="bg-white border h-100" style="border-radius:14px;border-color:#e9ecef !important;padding:20px;">
          <div class="d-flex align-items-center justify-content-between flex-wrap mb-3" style="gap:8px;">
            <p class="text-dark mb-0" style="font-weight:600;font-size:14px;">Projects <i class="bi bi-question-circle text-secondary" style="font-size:12px;" title="Customer engagements with a budget — profit/loss is their linked invoices minus their linked expenses."></i></p>
            <div class="d-flex align-items-center" style="gap:10px;">
              <a href="/projects" class="text-dark" style="font-size:12px;font-weight:600;">View all</a>
              <a href="/projects" class="btn btn-primary btn-sm" style="border-radius:8px;font-size:12px;">
                <i class="bi bi-plus-lg mr-1"></i>Create project
              </a>
            </div>
          </div>

          <div v-if="!projectStats || projectStats.total_projects === 0" class="text-secondary" style="font-size:12px;">No projects yet.</div>
          <div v-else class="row">
            <div class="col-6 col-md-3 mb-2">
              <p class="text-secondary mb-1" style="font-size:11px;">All</p>
              <p class="text-dark mb-0" style="font-size:18px;font-weight:700;">{{ projectStats.total_projects }}</p>
            </div>
            <div class="col-6 col-md-3 mb-2">
              <p class="text-secondary mb-1" style="font-size:11px;">Active</p>
              <p class="text-dark mb-0" style="font-size:18px;font-weight:700;">{{ projectStats.active_projects }}</p>
            </div>
            <div class="col-6 col-md-3 mb-2">
              <p class="text-secondary mb-1" style="font-size:11px;">Completed</p>
              <p class="text-dark mb-0" style="font-size:18px;font-weight:700;">{{ projectStats.completed_projects }}</p>
            </div>
            <div class="col-6 col-md-3 mb-2">
              <p class="text-secondary mb-1" style="font-size:11px;">Net P&amp;L</p>
              <p class="mb-0" style="font-size:18px;font-weight:700;" :class="projectStats.net_profit_loss >= 0 ? 'text-dark' : 'text-danger'">
                {{ formatCurrency(projectStats.net_profit_loss) }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { Chart, registerables } from 'chart.js';
import { mapGetters } from 'vuex';
Chart.register(...registerables);

export default {
  name: 'InvoiceOverviewDashboard',

  data() {
    return {
      loading: true,
      error: null,
      overview: null,
      projectStats: null,
      trendPeriod: 'last_12_months',
      breakdownPeriod: 'fiscal_year',
      comparisonPeriod: 'fiscal_year',
      accountingMethod: 'accrual',
      showCustomize: false,
      trendPeriodOptions: [
        { value: 'monthly', label: 'Monthly' },
        { value: 'quarterly', label: 'Quarterly' },
        { value: 'half_year', label: 'Half year' },
        { value: 'last_12_months', label: 'Last 12 months' },
        { value: 'mtd', label: 'Month to date' },
        { value: 'ytd', label: 'Year to date' },
        { value: 'fiscal_year', label: 'Fiscal year' },
      ],
      breakdownPeriodOptions: [
        { value: 'mtd', label: 'Month to date' },
        { value: 'ytd', label: 'Year to date' },
        { value: 'fiscal_year', label: 'Current fiscal year' },
      ],
      comparisonPeriodOptions: [
        { value: 'monthly', label: 'Month' },
        { value: 'quarterly', label: 'Quarter' },
        { value: 'half_year', label: 'Half year' },
        { value: 'fiscal_year', label: 'Fiscal year' },
      ],
      chartColors: ['#fd4719', '#1e293b', '#f59e0b', '#94a3b8', '#fbbf9e', '#334155'],
      charts: { cashFlow: null, pl: null, expense: null, netIncome: null },
    };
  },

  computed: {
    ...mapGetters(['userDetails']),

    greeting() {
      const hour = new Date().getHours();
      const time = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
      const name = this.userDetails?.name ? `, ${this.userDetails.name.split(' ')[0]}` : '';
      return `${time}${name}`;
    },

    companySettings() { return this.userDetails?.company?.companysettings || null; },
    overdueInvoices() { return this.overview?.overdue_invoices || []; },
    overdueBills()    { return this.overview?.overdue_bills || []; },
    receivables()     { return this.overview?.receivables || null; },
    payables()        { return this.overview?.payables || null; },
    currentMonth()    { return this.overview?.net_income?.current  || { income: 0, expense: 0, net_income: 0 }; },
    previousMonth()   { return this.overview?.net_income?.previous || { income: 0, expense: 0, net_income: 0 }; },

    expenseCategories() {
      const breakdown = this.overview?.expense_breakdown || {};
      const total = Object.values(breakdown).reduce((sum, v) => sum + v, 0);
      if (total <= 0) return [];
      return Object.entries(breakdown)
        .slice(0, 6)
        .map(([name, amount]) => ({ name, amount, percent: Math.round((amount / total) * 100) }));
    },
  },

  mounted() {
    this.fetchOverview();
    if (this.companySettings && this.companySettings.has_projects) this.fetchProjectStats();
    document.addEventListener('click', this.onClickOutsideCustomize);
  },

  beforeDestroy() {
    Object.values(this.charts).forEach(c => c && c.destroy());
    document.removeEventListener('click', this.onClickOutsideCustomize);
  },

  methods: {
    formatCurrency(value) {
      return 'KES ' + Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    overdueLabel(days) {
      if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
      if (days < 365) return `${Math.round(days / 30)} month${Math.round(days / 30) === 1 ? '' : 's'} ago`;
      return `${Math.round(days / 365)} year${Math.round(days / 365) === 1 ? '' : 's'} ago`;
    },

    onClickOutsideCustomize(e) {
      if (this.showCustomize && this.$refs.customizeWrap && !this.$refs.customizeWrap.contains(e.target)) {
        this.showCustomize = false;
      }
    },

    setAccountingMethod(method) {
      if (this.accountingMethod === method) {
        this.showCustomize = false;
        return;
      }
      this.accountingMethod = method;
      this.showCustomize = false;
      this.fetchOverview();
    },

    agingRows(summary) {
      if (!summary) return [];
      return [
        { label: 'Coming due',      amount: summary.current?.amount ?? 0 },
        { label: '1-30 days',       amount: summary.bucket_1_30?.amount ?? 0 },
        { label: '31-60 days',      amount: summary.bucket_31_60?.amount ?? 0 },
        { label: '61-90 days',      amount: summary.bucket_61_90?.amount ?? 0 },
        { label: '> 90 days',       amount: summary.bucket_90_plus?.amount ?? 0 },
      ];
    },

    async fetchOverview() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get('/api/reports/finance/overview', {
          params: {
            trend_period: this.trendPeriod,
            breakdown_period: this.breakdownPeriod,
            comparison_period: this.comparisonPeriod,
            accounting_method: this.accountingMethod,
          },
        });
        this.overview = data;
      } catch (e) {
        this.error = e.response?.data?.message || 'Please try again in a moment.';
      } finally {
        this.loading = false;
        // The canvases live behind v-else="loading" — they don't exist in
        // the DOM at all until loading flips false. nextTick alone waits
        // for Vue's patch, not for the browser to actually paint/lay out
        // the new elements; requestAnimationFrame on top of it is the
        // standard chart.js-in-Vue guard against measuring a 0-height
        // canvas. Runs even on error/no-overview — renderCharts()
        // already no-ops per-chart when a ref or its data isn't there.
        this.$nextTick(() => requestAnimationFrame(this.renderCharts));
      }
    },

    async fetchProjectStats() {
      try {
        const { data } = await axios.get('/api/projects-summary');
        this.projectStats = data.data;
      } catch (e) {}
    },

    renderCharts() {
      // Two distinct series from the backend, not one reused twice: cash_flow
      // is real cash in/out (payments actually received/paid), profit_and_loss
      // is accrual revenue/expense — conflating them was the exact "Cash Flow
      // is literally the P&L series" bug this app's accounting audit flagged.
      const cashFlow = this.overview?.cash_flow || [];
      const pl = this.overview?.profit_and_loss || [];
      const labels = pl.map(m => m.label);

      Object.values(this.charts).forEach(c => c && c.destroy());

      const bar = (label, data, color) => ({ label, data, backgroundColor: color, borderRadius: 4, maxBarThickness: 36 });

      // Each chart is created in its own try/catch — these used to all
      // live in one block, so a single bad canvas/data point threw and
      // silently skipped every chart after it (all 4 blank at once was
      // exactly this: one exception early on, nothing else ever ran).
      try {
        if (this.$refs.cashFlowCanvas) {
          this.charts.cashFlow = new Chart(this.$refs.cashFlowCanvas.getContext('2d'), {
            type: 'bar',
            data: {
              labels: cashFlow.map(m => m.label),
              datasets: [
                bar('Inflow', cashFlow.map(m => m.income), '#fd4719'),
                bar('Outflow', cashFlow.map(m => m.expense), '#e2e8f0'),
              ],
            },
            options: this.barOptions(),
          });
        }
      } catch (e) { console.error('Cash flow chart failed to render', e); }

      try {
        if (this.$refs.plCanvas) {
          this.charts.pl = new Chart(this.$refs.plCanvas.getContext('2d'), {
            type: 'bar',
            data: {
              labels,
              datasets: [
                bar('Income', pl.map(m => m.income), '#fd4719'),
                bar('Expenses', pl.map(m => m.expense), '#1e293b'),
              ],
            },
            options: this.barOptions(),
          });
        }
      } catch (e) { console.error('Profit and loss chart failed to render', e); }

      try {
        if (this.$refs.expenseCanvas && this.expenseCategories.length) {
          this.charts.expense = new Chart(this.$refs.expenseCanvas.getContext('2d'), {
            type: 'doughnut',
            data: {
              labels: this.expenseCategories.map(c => c.name),
              datasets: [{ data: this.expenseCategories.map(c => c.amount), backgroundColor: this.chartColors, borderWidth: 2, borderColor: '#fff' }],
            },
            options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: this.tooltipStyle() }, cutout: '70%' },
          });
        }
      } catch (e) { console.error('Expenses breakdown chart failed to render', e); }

      try {
        if (this.$refs.netIncomeCanvas) {
          this.charts.netIncome = new Chart(this.$refs.netIncomeCanvas.getContext('2d'), {
            type: 'bar',
            data: {
              labels: ['Previous', 'Current'],
              datasets: [bar('Net income', [this.previousMonth.net_income, this.currentMonth.net_income], '#fd4719')],
            },
            options: this.barOptions(),
          });
        }
      } catch (e) { console.error('Net income chart failed to render', e); }
    },

    tooltipStyle() {
      return {
        backgroundColor: '#1e293b',
        titleColor: '#fff',
        bodyColor: 'rgba(255,255,255,0.7)',
        padding: 10,
        cornerRadius: 6,
        callbacks: { label: (ctx) => ` ${ctx.dataset.label || ctx.label}: ${this.formatCurrency(ctx.parsed.y ?? ctx.parsed)}` },
      };
    },

    barOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 200 },
        plugins: {
          // Cash flow and P&L already render their own custom HTML legend
          // (the colored dots above each chart) — chart.js's built-in one
          // would just duplicate it.
          legend: { display: false },
          tooltip: this.tooltipStyle(),
        },
        scales: {
          x: { grid: { display: false }, border: { display: false }, ticks: { font: { size: 11 }, color: '#94a3b8' } },
          y: {
            grid: { color: 'rgba(0,0,0,0.04)' },
            border: { display: false },
            ticks: { font: { size: 11 }, color: '#94a3b8', callback: v => (Math.abs(v) >= 1000 ? (v / 1000).toFixed(0) + 'K' : v) },
          },
        },
        interaction: { intersect: false, mode: 'index' },
      };
    },
  },
};
</script>

<style scoped>
/* chart.js sets its own width/height attributes on <canvas> for device-
   pixel-ratio scaling — unlike <img>, canvas has no built-in max-width
   safety net, so a chart can push its card (and the page) wider than
   intended unless this is set explicitly. */
canvas {
  max-width: 100%;
}
.ov-overdue-row {
  text-decoration: none;
  cursor: pointer;
  border-radius: 6px;
  margin: 0 -8px;
  padding-left: 8px !important;
  padding-right: 8px !important;
  transition: background-color 0.15s;
}
.ov-overdue-row:hover {
  background-color: #f8fafc;
}
.ov-quick-action {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  border-radius: 10px;
  padding: 14px 14px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition: filter 0.15s;
}
.ov-quick-action:hover {
  filter: brightness(0.96);
  text-decoration: none;
}
.ov-period-select {
  width: auto;
  display: inline-block;
  border-radius: 20px;
  font-size: 12px;
  padding: 4px 10px;
}
.ov-customize-panel {
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  width: 280px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  padding: 14px;
  z-index: 20;
}
.ov-customize-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 4px;
  font-size: 13px;
  color: #0f172a;
  text-decoration: none;
  border-top: 1px solid #f1f5f9;
}
.ov-customize-option:first-of-type {
  border-top: none;
  margin-top: 6px;
}
.ov-customize-option:hover {
  color: #fd4719;
  text-decoration: none;
}
</style>
